import React from "react"
import {Link} from "react-router-dom"
import {Prism as SyntaxHighlighter} from "react-syntax-highlighter"
import {dracula} from "react-syntax-highlighter/dist/cjs/styles/prism"

import globalStyles from "../../../../App.module.scss"
import {Arrow, Box, Diagram, DiagramCaption} from "../../Architecture/shared/Diagram"

const uniqueNodeIdentity = `# Give every node its own hostname
sudo hostnamectl set-hostname k8s-worker-1

# A cloned VM keeps the template's machine-id, which can make DHCP hand out the same IP twice
sudo rm -f /etc/machine-id /var/lib/dbus/machine-id
sudo systemd-machine-id-setup

# MAC address and product_uuid must also differ between nodes
ip link
sudo cat /sys/class/dmi/id/product_uuid`

const disableSwap = `sudo swapoff -a
# Keep it off after a reboot
sudo sed -i '/ swap / s/^#*/#/' /etc/fstab`

const kernelNetworking = `# Load the modules container networking relies on, now and on every boot
cat <<EOF | sudo tee /etc/modules-load.d/k8s.conf
overlay
br_netfilter
EOF
sudo modprobe overlay
sudo modprobe br_netfilter

# Let bridged pod traffic pass through iptables and allow the node to route packets
cat <<EOF | sudo tee /etc/sysctl.d/k8s.conf
net.bridge.bridge-nf-call-iptables  = 1
net.bridge.bridge-nf-call-ip6tables = 1
net.ipv4.ip_forward                 = 1
EOF
sudo sysctl --system`

const installContainerd = `sudo apt update
sudo apt install -y containerd

# Write containerd's full default config to the file
sudo mkdir -p /etc/containerd
containerd config default | sudo tee /etc/containerd/config.toml > /dev/null

# Switch runc to the systemd cgroup driver (the kubelet uses systemd too)
sudo sed -i 's/SystemdCgroup = false/SystemdCgroup = true/' /etc/containerd/config.toml

# Apply and verify
sudo systemctl restart containerd
sudo containerd config dump | grep SystemdCgroup`

const installKubeadm = `# Pick the Kubernetes minor version you want, e.g. v1.34 (see kubernetes.io/releases)
K8S_MINOR=v1.34

sudo apt update
sudo apt install -y apt-transport-https ca-certificates curl gpg
sudo mkdir -p -m 755 /etc/apt/keyrings
curl -fsSL https://pkgs.k8s.io/core:/stable:/$K8S_MINOR/deb/Release.key \\
  | sudo gpg --dearmor -o /etc/apt/keyrings/kubernetes-apt-keyring.gpg
echo "deb [signed-by=/etc/apt/keyrings/kubernetes-apt-keyring.gpg] https://pkgs.k8s.io/core:/stable:/$K8S_MINOR/deb/ /" \\
  | sudo tee /etc/apt/sources.list.d/kubernetes.list

sudo apt update
sudo apt install -y kubelet kubeadm kubectl
# Stop apt upgrade from bumping them, upgrades go through kubeadm
sudo apt-mark hold kubelet kubeadm kubectl
sudo systemctl enable --now kubelet`

const kubeadmInit = `# The pod CIDR must match the one configured in Calico later,
# and must not overlap the LAN (192.168.50.0/24) or the service CIDR (10.96.0.0/12)
sudo kubeadm init --pod-network-cidr=10.10.0.0/16`

const configureKubectl = `mkdir -p $HOME/.kube
sudo cp -i /etc/kubernetes/admin.conf $HOME/.kube/config
sudo chown $(id -u):$(id -g) $HOME/.kube/config

# Optional: manage the cluster from your laptop instead of SSH-ing into the control plane
scp user@192.168.50.190:.kube/config ~/.kube/config`

const installCalico = `# Check the Calico docs for the current release
CALICO_VERSION=v3.30.0

# The operator CRDs are too big for the last-applied annotation: use create, not apply.
# Newer releases ship the CRDs separately as operator-crds.yaml; create it first if the docs list it.
kubectl create -f https://raw.githubusercontent.com/projectcalico/calico/$CALICO_VERSION/manifests/tigera-operator.yaml

curl -O https://raw.githubusercontent.com/projectcalico/calico/$CALICO_VERSION/manifests/custom-resources.yaml
# Edit spec.calicoNetwork.ipPools[0].cidr to 10.10.0.0/16 (same as --pod-network-cidr)
kubectl create -f custom-resources.yaml

# Wait until every component reports Available
watch kubectl get tigerastatus`

const joinWorkers = `# Run on each worker as root, with the command printed by kubeadm init
sudo kubeadm join 192.168.50.190:6443 --token <token> \\
  --discovery-token-ca-cert-hash sha256:<hash>

# The token expires after 24 hours. To get a fresh join command, on the control plane:
kubeadm token create --print-join-command`

const verifyCluster = `kubectl get nodes -o wide
kubectl get pods -A

# Optional: show a role for the workers in "kubectl get nodes"
kubectl label node k8s-worker-1 node-role.kubernetes.io/worker=
kubectl label node k8s-worker-2 node-role.kubernetes.io/worker=`

const installMetricsServer = `kubectl apply -f https://github.com/kubernetes-sigs/metrics-server/releases/latest/download/components.yaml

# Lab shortcut: kubeadm's kubelet serving certificates are self-signed, so let metrics-server skip verification
kubectl patch deployment metrics-server -n kube-system --type json \\
  -p '[{"op":"add","path":"/spec/template/spec/containers/0/args/-","value":"--kubelet-insecure-tls"}]'

kubectl top nodes`

const installLocalPath = `kubectl apply -f https://raw.githubusercontent.com/rancher/local-path-provisioner/master/deploy/local-path-storage.yaml

# Make it the default, so PVCs without a storageClassName get a volume
kubectl patch storageclass local-path \\
  -p '{"metadata":{"annotations":{"storageclass.kubernetes.io/is-default-class":"true"}}}'`

const localPathStorageClass = `apiVersion: storage.k8s.io/v1
kind: StorageClass
metadata:
  name: local-path
  annotations:
    storageclass.kubernetes.io/is-default-class: "true"
provisioner: rancher.io/local-path
reclaimPolicy: Delete
allowVolumeExpansion: true
volumeBindingMode: WaitForFirstConsumer`

const persistentVolumeClaim = `apiVersion: v1
kind: PersistentVolumeClaim
metadata:
  name: pv-claim
spec:
  accessModes:
    - ReadWriteOnce
  resources:
    requests:
      storage: 1Gi`

const installTraefik = `helm repo add traefik https://traefik.github.io/charts
helm repo update

# Every option the chart supports, to pick from when writing traefik-values.yaml
helm show values traefik/traefik > traefik-defaults.yaml

helm install traefik traefik/traefik \\
  --namespace traefik --create-namespace \\
  -f traefik-values.yaml`

const traefikValues = `service:
  type: NodePort

ports:
  web:
    nodePort: 30080`

const harborPullSecret = `# Single quotes matter: the robot account name contains a $
kubectl create secret docker-registry harbor-pull \\
  --docker-server=registry.julcap.net \\
  --docker-username='robot$k8s-user' \\
  --docker-password='<robot-account-secret>'`

const privateImagePod = `apiVersion: v1
kind: Pod
metadata:
  name: test
  labels:
    run: test
spec:
  volumes:
    - name: my-storage
      persistentVolumeClaim:
        claimName: pv-claim
  containers:
    - name: busybox-test-pv
      image: registry.julcap.net/sandbox/busybox:1.28
      volumeMounts:
        - mountPath: "/data"
          name: my-storage
      command: ["sh", "-c", "echo hello >> /data/test.txt && sleep 3600"]
  imagePullSecrets:
    - name: harbor-pull
  restartPolicy: Always`

const debugDns = `# Temporary pod, deleted automatically when you type exit (--rm)
kubectl run tmp --image=busybox:1.28 --rm -it -- sh

# Inside the pod
nslookup kubernetes.default
nslookup nginx.default
wget -qO- http://<nginx-pod-ip>   # -q quiet, -O- print to screen instead of a file

# If names do not resolve, check CoreDNS
kubectl get pods -n kube-system -l k8s-app=kube-dns
kubectl logs -n kube-system -l k8s-app=kube-dns`

const day2Operations = `# Certificates issued by kubeadm expire after one year
sudo kubeadm certs check-expiration
sudo kubeadm certs renew all   # then restart the control-plane static pods (or reboot the node)

# Upgrades: one minor version at a time, control plane first
sudo kubeadm upgrade plan
sudo kubeadm upgrade apply v1.35.x   # on the control plane
sudo kubeadm upgrade node            # on each worker, after kubectl drain <node> --ignore-daemonsets

# Start over on a node if init/join went wrong
sudo kubeadm reset
sudo rm -rf /etc/cni/net.d $HOME/.kube/config`

function CodeBlock({children, language = "bash"}) {
    return (
        <div style={{maxWidth: "800px"}}>
            <SyntaxHighlighter
                children={children}
                style={dracula}
                PreTag="div"
                language={language}
            />
        </div>
    )
}

export default function KubeadmClusterSetup() {
    return (
        <div className={globalStyles.article}>
            <h1>Kubernetes cluster from scratch with kubeadm</h1>

            <p>Notes from building a three-node cluster on Proxmox: one control plane and two workers, each an
                Ubuntu VM, running containerd, Calico for pod networking, metrics-server, local-path storage and
                Traefik for ingress. The order matters: every node gets the same base setup, then the control
                plane is initialised, the network plugin installed, and only then the workers join.</p>

            <Diagram viewBox="0 0 780 220">
                <Box x={20} y={88} width={130} height={44} label="Laptop" sublabel="kubectl, helm"/>
                <Box x={250} y={88} width={190} height={44} label="k8s-cp-1" sublabel="192.168.50.190 · API :6443"
                     accent/>
                <Box x={570} y={30} width={180} height={44} label="k8s-worker-1" sublabel="kubelet · containerd"/>
                <Box x={570} y={146} width={180} height={44} label="k8s-worker-2" sublabel="kubelet · containerd"/>

                <Arrow x1={150} y1={110} x2={250} y2={110} label="kubeconfig"/>
                <Arrow x1={570} y1={52} x2={440} y2={100} label="join"/>
                <Arrow x1={570} y1={168} x2={440} y2={120} label="join"/>
            </Diagram>
            <DiagramCaption>All three VMs live on the same Proxmox bridge and LAN. Workers only ever talk to the
                API server on the control plane; kubectl does the same from outside the cluster.</DiagramCaption>

            <h3>Before you start</h3>
            <ul>
                <li><b>Sizing:</b> at least 2 vCPU and 2 GB RAM per node (kubeadm refuses fewer than 2 CPUs on
                    the control plane). 4 GB on the workers leaves room for real workloads.</li>
                <li><b>Fixed IPs:</b> static addresses or DHCP reservations. The API server certificate is
                    issued for the control plane&apos;s IP, so changing it later is painful.</li>
                <li><b>Unique identity:</b> when cloning VMs from a template, each node needs its own hostname,
                    MAC address, product_uuid and machine-id.</li>
            </ul>
            <CodeBlock>{uniqueNodeIdentity}</CodeBlock>

            <h3>1. All nodes: base setup</h3>
            <h4>Disable swap</h4>
            <p>By default the kubelet will not start while swap is enabled.</p>
            <CodeBlock>{disableSwap}</CodeBlock>

            <h4>Kernel modules and IP forwarding</h4>
            <CodeBlock>{kernelNetworking}</CodeBlock>

            <h4>Firewall ports</h4>
            <p>Ubuntu cloud images ship with ufw disabled. If you enable it, these must be open between the
                nodes:</p>
            <div className={globalStyles.styledTable}>
                <table style={{minWidth: "550px"}}>
                    <thead>
                    <tr>
                        <th>Node</th>
                        <th>Port</th>
                        <th>Used by</th>
                    </tr>
                    </thead>
                    <tbody>
                    <tr>
                        <td>Control plane</td>
                        <td><code>6443/tcp</code></td>
                        <td>Kubernetes API server</td>
                    </tr>
                    <tr>
                        <td>Control plane</td>
                        <td><code>2379-2380/tcp</code></td>
                        <td>etcd</td>
                    </tr>
                    <tr>
                        <td>Control plane</td>
                        <td><code>10257, 10259/tcp</code></td>
                        <td>controller-manager, scheduler</td>
                    </tr>
                    <tr>
                        <td>All</td>
                        <td><code>10250/tcp</code></td>
                        <td>kubelet API (logs, exec, metrics-server)</td>
                    </tr>
                    <tr>
                        <td>Workers</td>
                        <td><code>30000-32767/tcp</code></td>
                        <td>NodePort services (Traefik on 30080)</td>
                    </tr>
                    <tr>
                        <td>All</td>
                        <td><code>179/tcp, 4789/udp</code></td>
                        <td>Calico BGP and VXLAN</td>
                    </tr>
                    </tbody>
                </table>
            </div>

            <h4>Container runtime: containerd</h4>
            <p>The kubelet and the container runtime must agree on the cgroup driver. Both use systemd here; a
                mismatch shows up as pods restarting for no apparent reason.</p>
            <CodeBlock>{installContainerd}</CodeBlock>
            <p>If <em>kubeadm init</em> warns that the sandbox (pause) image of the runtime is inconsistent,
                set the sandbox image in <em>config.toml</em> to the version kubeadm expects
                (<em>kubeadm config images list</em>) and restart containerd.</p>

            <h4>kubeadm, kubelet and kubectl</h4>
            <p>Install all three with the same version and pin them. The kubelet restarts every few seconds
                until <em>kubeadm init</em> or <em>join</em> gives it a configuration — that is expected.</p>
            <CodeBlock>{installKubeadm}</CodeBlock>

            <h3>2. Control plane: initialise the cluster</h3>
            <CodeBlock>{kubeadmInit}</CodeBlock>
            <p>If you might add more control planes later, also pass
                <em> --control-plane-endpoint</em> with a DNS name instead of relying on the node&apos;s IP. It
                is hard to add after the cluster exists.</p>
            <p>When init succeeds it prints a <em>kubeadm join</em> command for the workers. Keep it, then set
                up kubectl for your user:</p>
            <CodeBlock>{configureKubectl}</CodeBlock>
            <p><em>admin.conf</em> is full cluster-admin access, so treat it like a root password.</p>

            <h3>3. Pod network: Calico</h3>
            <p>Until a CNI plugin is installed, nodes stay <em>NotReady</em> and CoreDNS stays
                <em> Pending</em>. Follow the <a
                    href="https://docs.tigera.io/calico/latest/getting-started/kubernetes/self-managed-onprem/onpremises"
                    target="_blank" rel="noreferrer">Calico on-premises guide</a>; the steps look like this:</p>
            <CodeBlock>{installCalico}</CodeBlock>

            <h3>4. Workers: join the cluster</h3>
            <CodeBlock>{joinWorkers}</CodeBlock>
            <CodeBlock>{verifyCluster}</CodeBlock>
            <p>All nodes should be <em>Ready</em> and every pod in <em>kube-system</em> and
                <em> calico-system</em> <em>Running</em>.</p>

            <h3>5. Add-ons</h3>
            <h4>Metrics server</h4>
            <p><a href="https://github.com/kubernetes-sigs/metrics-server" target="_blank"
                  rel="noreferrer">metrics-server</a> powers <em>kubectl top</em> and the Horizontal Pod
                Autoscaler. On a kubeadm cluster it fails with x509 errors out of the box, because the kubelets
                serve self-signed certificates.</p>
            <CodeBlock>{installMetricsServer}</CodeBlock>
            <p>To fix this without skipping TLS, set <em>serverTLSBootstrap: true</em> in the kubelet config
                (the <em>kubelet-config</em> ConfigMap and <em>/var/lib/kubelet/config.yaml</em> on each node),
                restart the kubelets, and approve their certificate requests with <em>kubectl get csr</em> and
                <em> kubectl certificate approve</em>.</p>

            <h4>Storage: local-path provisioner</h4>
            <p>kubeadm ships no storage, so PVCs stay <em>Pending</em> until something provisions volumes.
                Rancher&apos;s local-path provisioner creates them as folders on the node&apos;s disk:</p>
            <CodeBlock>{installLocalPath}</CodeBlock>
            <p>The resulting StorageClass:</p>
            <CodeBlock language="yaml">{localPathStorageClass}</CodeBlock>
            <p>A claim using it:</p>
            <CodeBlock language="yaml">{persistentVolumeClaim}</CodeBlock>
            <p>The data lives on a single node and is not replicated. <em>WaitForFirstConsumer</em> creates
                the volume on whichever node the pod gets scheduled to, and that pod is bound to that node from
                then on. Fine for a lab; use NFS, Longhorn or Ceph when the data matters.</p>

            <h4>Ingress: Traefik</h4>
            <p>Bare-metal clusters have no cloud load balancer, so a <em>LoadBalancer</em> Service would stay
                <em> Pending</em>. Exposing Traefik as a NodePort is the simplest option: it becomes reachable at
                <em> http://&lt;any-node-ip&gt;:30080</em>. MetalLB is the next step if you want real
                LoadBalancer IPs on the LAN.</p>
            <CodeBlock>{installTraefik}</CodeBlock>
            <p><em>traefik-values.yaml</em>:</p>
            <CodeBlock language="yaml">{traefikValues}</CodeBlock>

            <h3>6. Pull images from a private registry</h3>
            <p>Create a robot account in Harbor and store its credentials in a secret, in the same namespace
                as the pods that will use it:</p>
            <CodeBlock>{harborPullSecret}</CodeBlock>
            <p>Reference the image with the full registry domain and add the secret under
                <em> imagePullSecrets</em>:</p>
            <CodeBlock language="yaml">{privateImagePod}</CodeBlock>
            <p>To keep the password out of your shell history, and to attach the secret to a service account
                instead of every pod, see <Link to="/kubernetes/registry-pull-secret">Private registry pull
                    secret</Link>.</p>

            <h3>7. Debugging DNS</h3>
            <p>Use <em>busybox:1.28</em>. Newer busybox images handle search domains badly, so short names like
                <em> nginx.default</em> fail even when DNS works fine.</p>
            <CodeBlock>{debugDns}</CodeBlock>

            <h3>8. Keeping it running</h3>
            <ul>
                <li><b>Certificates:</b> kubeadm certificates are valid for one year. Upgrading the cluster
                    renews them; otherwise renew them manually before they expire.</li>
                <li><b>Upgrades:</b> skip no minor versions. Change <em>K8S_MINOR</em> in the apt source,
                    unhold and upgrade kubeadm, run the upgrade, then upgrade kubelet and kubectl on each node
                    while it is drained.</li>
                <li><b>Backups:</b> etcd holds the whole cluster state. Back up an <em>etcdctl snapshot</em> and
                    <em> /etc/kubernetes/pki</em>; a Proxmox snapshot of the control-plane VM is a cheap extra
                    safety net before upgrades.</li>
            </ul>
            <CodeBlock>{day2Operations}</CodeBlock>
        </div>
    )
}
